import React, { useEffect, useState } from 'react';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { BuildingOption } from '../../api/feeCategoriesApi';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { permissionGroupsApi, PermissionGroup } from '../../api/permissionGroupsApi';
import { feedbacksApi, FeedbackStats } from '../../api/feedbacksApi';

const FeedbackStatistics: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [staffList, setStaffList] = useState<{ id: string; name: string }[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [permissionGroupId, setPermissionGroupId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setFromDate(start.toISOString().slice(0, 10));
    setToDate(now.toISOString().slice(0, 10));

    fetch(`${API_BASE_URL}/buildings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setBuildings(data);
        setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
      })
      .catch(console.error);

    permissionGroupsApi.getAll().then(setPermissionGroups).catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedBuildingId) return;
    fetch(`${API_BASE_URL}/users/staff`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setStaffList)
      .catch(console.error);
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedBuildingId) loadStats();
  }, [selectedBuildingId, fromDate, toDate, permissionGroupId, staffId]);

  const loadStats = async () => {
    if (!selectedBuildingId) return;
    setLoading(true);
    try {
      const data = await feedbacksApi.getStats({
        buildingId: selectedBuildingId,
        fromDate,
        toDate,
        permissionGroupId: permissionGroupId || undefined,
        staffId: staffId || undefined,
      });
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({
    label,
    value,
    change,
    highlight,
  }: {
    label: string;
    value: number | string;
    change?: number;
    highlight?: 'red' | 'blue' | 'green';
  }) => (
    <div
      className={`rounded-lg border p-4 ${
        highlight === 'red'
          ? 'bg-red-50 border-red-200'
          : highlight === 'blue'
            ? 'bg-blue-50 border-blue-200'
            : 'bg-white border-gray-200'
      }`}
    >
      <div className="text-3xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-600 mt-1">{label}</div>
      {change !== undefined && (
        <div className={`text-xs mt-1 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">{t('cskhDashboard')}</h1>

      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-4 items-end">
        {shouldShowBuildingSelector(buildings) && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('selectBuilding')}</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[200px]"
            >
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Từ ngày</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Đến ngày</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('allDepartments')}</label>
          <select value={permissionGroupId} onChange={(e) => setPermissionGroupId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[180px]">
            <option value="">{t('allDepartments')}</option>
            {permissionGroups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('allStaff')}</label>
          <select value={staffId} onChange={(e) => setStaffId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[180px]">
            <option value="">{t('allStaff')}</option>
            {staffList.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('loading')}...</div>
      ) : stats ? (
        <>
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('currentOverview')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MetricCard label={t('pendingBacklog')} value={stats.overview.pending} highlight="blue" />
              <MetricCard label={t('overdueSla')} value={stats.overview.overdueSla} highlight="red" />
            </div>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('performanceInPeriod')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label={t('createdInPeriod')} value={stats.performance.created.value} change={stats.performance.created.change} />
              <MetricCard label={t('closedByCreationPeriod')} value={stats.performance.closedByCreationPeriod.value} change={stats.performance.closedByCreationPeriod.change} />
              <MetricCard label={t('completionRate')} value={`${stats.performance.completionRate.value}%`} />
              <MetricCard label={t('closedInPeriod')} value={stats.performance.closedInPeriod.value} change={stats.performance.closedInPeriod.change} />
              <MetricCard label={t('slaComplianceRate')} value={`${stats.performance.slaComplianceRate.value}%`} />
              <MetricCard label={t('newRequests')} value={stats.performance.newRequests.value} />
              <MetricCard label={t('feedbackProcessingMetric')} value={stats.performance.processing.value} />
              <MetricCard label={t('paused')} value={stats.performance.paused.value} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">{t('openTicketAge')}</h3>
              <div className="space-y-2">
                {stats.charts.openAgeBuckets.map((bucket, i) => {
                  const max = Math.max(...stats.charts.openAgeBuckets.map((b) => b.count), 1);
                  const colors = ['bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'];
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-sm w-28 shrink-0">{bucket.label}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className={`h-full ${colors[i]} rounded-full flex items-center px-2 text-xs text-white`}
                          style={{ width: `${Math.max((bucket.count / max) * 100, bucket.count > 0 ? 8 : 0)}%` }}
                        >
                          {bucket.count > 0 ? bucket.count : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">{t('requestsByCategory')}</h3>
              <div className="space-y-2">
                {stats.charts.categoryBreakdown.map((item, i) => (
                  <div key={i} className="flex justify-between items-center text-sm border-b pb-2">
                    <span>{item.category}</span>
                    <span className="font-medium">{item.count} ({item.percentage}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <h3 className="font-semibold p-4 border-b">{t('departmentPerformance')}</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Phòng ban</th>
                    <th className="px-4 py-2 text-left">Tổng</th>
                    <th className="px-4 py-2 text-left">Đã xử lý</th>
                    <th className="px-4 py-2 text-left">{t('onTimeSla')}</th>
                    <th className="px-4 py-2 text-left">{t('slaViolations')}</th>
                    <th className="px-4 py-2 text-left">Tỷ lệ SLA</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.departmentPerformance.map((row, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-4 py-2">{i + 1}</td>
                      <td className="px-4 py-2">{row.department}</td>
                      <td className="px-4 py-2">{row.total}</td>
                      <td className="px-4 py-2">{row.processed}</td>
                      <td className="px-4 py-2">{row.onTimeSla}</td>
                      <td className="px-4 py-2">{row.slaViolations}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${row.slaRate >= 75 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {row.slaRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <h3 className="font-semibold p-4 border-b">{t('staffPerformance')}</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Nhân viên</th>
                    <th className="px-4 py-2 text-left">Phòng ban</th>
                    <th className="px-4 py-2 text-left">Tổng</th>
                    <th className="px-4 py-2 text-left">Đã xử lý</th>
                    <th className="px-4 py-2 text-left">Tỷ lệ SLA</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.staffPerformance.map((row, i) => (
                    <tr key={row.staffId} className="border-t">
                      <td className="px-4 py-2">{i + 1}</td>
                      <td className="px-4 py-2">{row.staffName}</td>
                      <td className="px-4 py-2">{row.department}</td>
                      <td className="px-4 py-2">{row.total}</td>
                      <td className="px-4 py-2">{row.processed}</td>
                      <td className="px-4 py-2">{row.slaRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-4">{t('averageProcessingTimes')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label={t('firstResponseTime')} value={`${stats.averageTimes.firstResponseHours}h`} />
              <MetricCard label={t('averageProcessingTime')} value={`${stats.averageTimes.averageProcessingHours}h`} />
              <MetricCard label={t('closeRequestTime')} value={`${stats.averageTimes.closeRequestHours}h`} />
              <MetricCard label={t('averageSlaViolationTime')} value={`${stats.averageTimes.averageSlaViolationHours}h`} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-700">{stats.slaAlerts.violating}</div>
              <div className="text-sm text-red-600">{t('violatingSla')}</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-700">{stats.slaAlerts.approaching}</div>
              <div className="text-sm text-yellow-600">{t('approachingSla')}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">{stats.quality.satisfactionRate}%</div>
              <div className="text-sm text-blue-600">{t('satisfactionRate')}</div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default FeedbackStatistics;
